import type { Dayjs } from "dayjs";

import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
// oxlint-disable-next-line import/no-duplicates
import relativeTime from "dayjs/plugin/relativeTime";
import updateLocale from "dayjs/plugin/updateLocale";
import weekOfYear from "dayjs/plugin/weekOfYear";

import { singleton } from "./memoize.ts";
import { getEnv } from "./solvariEnv.ts";

import "dayjs/locale/fr";
import "dayjs/locale/nl";
// The default import above is elided from the emitted .d.ts (it's only used as
// a value), so this side-effect import preserves the relativeTime type
// augmentation (.fromNow()) for consumers of @solvari/utils.
import "dayjs/plugin/relativeTime";

type SDayjs = Dayjs & {
  week(): number;
  week(value: number): SDayjs;
};

type DayjsFn = (date?: dayjs.ConfigType) => SDayjs;

const dayjsInstance = singleton(() => {
  dayjs.extend(relativeTime);
  dayjs.extend(updateLocale);
  dayjs.extend(LocalizedFormat);
  dayjs.extend(weekOfYear);

  dayjs.locale(getEnv().config.localeCode.slice(-2));

  dayjs.updateLocale("nl", {
    calendar: {
      lastDay: "[Gisteren]",
      sameDay: "[Vandaag]",
      nextDay: "[Morgen]",
      lastWeek: "dd D MMMM",
      nextWeek: "dd D MMMM",
      sameElse: "dd D MMMM",
    },
  });

  dayjs.updateLocale("fr", {
    calendar: {
      lastDay: "[Hier]",
      sameDay: "[Aujourd'hui]",
      nextDay: "[Demain]",
      lastWeek: "dd D MMMM",
      nextWeek: "dd D MMMM",
      sameElse: "dd D MMMM",
    },
  });

  return dayjs;
});

const sDayjs: DayjsFn = (date) => dayjsInstance()(date);

function toDate(value: Date | number | string): string {
  if (!value) {
    return value.toString();
  }
  return sDayjs(value.toString()).format("DD-MM-YYYY");
}

function toDateMedium(value: Date | number | string): string {
  if (!value) {
    return value.toString();
  }
  return sDayjs(value.toString()).format("D MMM YYYY");
}

function toDateTime(value: Date | number | string): string {
  if (!value) {
    return value.toString();
  }
  return sDayjs(value.toString()).format("DD-MM-YYYY HH:mm:ss");
}

function toCountdown(value: number): string {
  dayjs.extend(duration);
  return dayjs.duration(value, "seconds").format("mm:ss");
}

export type { SDayjs as Dayjs };
export { sDayjs as dayjs, toCountdown, toDate, toDateMedium, toDateTime };
